import type { BuildingDefinition, BuildingKind, ZoneType } from '../types/core';
import { pick } from '../utils/random';

const definitions: readonly BuildingDefinition[] = [
  {
    kind: 'small-terrace',
    label: 'Brick Terrace',
    zone: 'residential',
    capacity: 3,
    jobs: 0,
    footprintHint: '1x1',
    baseCost: 120,
    colours: { wall: '#a95b3d', roof: '#303942', trim: '#f2ddbf', accent: '#e6a83f' }
  },
  {
    kind: 'large-terrace',
    label: 'Town Terrace',
    zone: 'residential',
    capacity: 4,
    jobs: 0,
    footprintHint: '1x2',
    baseCost: 170,
    colours: { wall: '#bf6b42', roof: '#2d3844', trim: '#fff3d6', accent: '#557f69' }
  },
  {
    kind: 'semi-detached',
    label: 'Semi-Detached Home',
    zone: 'residential',
    capacity: 5,
    jobs: 0,
    footprintHint: '2x1',
    baseCost: 210,
    colours: { wall: '#b96a4d', roof: '#3c4148', trim: '#f5ead4', accent: '#617d47' }
  },
  {
    kind: 'detached-suburban',
    label: 'Detached Home',
    zone: 'residential',
    capacity: 5,
    jobs: 0,
    footprintHint: '1x1',
    baseCost: 260,
    colours: { wall: '#d7b57b', roof: '#4a4a4a', trim: '#fff0cf', accent: '#a95b3d' }
  },
  {
    kind: 'housing-estate',
    label: 'Modern Housing Estate',
    zone: 'residential',
    capacity: 8,
    jobs: 0,
    footprintHint: '2x2',
    baseCost: 420,
    colours: { wall: '#c98b63', roof: '#44535a', trim: '#e9ddc5', accent: '#87a96b' }
  },
  {
    kind: 'flats',
    label: 'Low-Rise Flats',
    zone: 'residential',
    capacity: 14,
    jobs: 0,
    footprintHint: '2x2',
    baseCost: 600,
    colours: { wall: '#b67f57', roof: '#36424c', trim: '#efe1c5', accent: '#4e7d8b' }
  },
  {
    kind: 'cafe',
    label: 'Cafe',
    zone: 'commercial',
    capacity: 0,
    jobs: 4,
    footprintHint: '1x1',
    baseCost: 250,
    colours: { wall: '#9f5e45', roof: '#2f3f4a', trim: '#f8e8c7', accent: '#f3c66f' }
  },
  {
    kind: 'bakery',
    label: 'Bakery',
    zone: 'commercial',
    capacity: 0,
    jobs: 5,
    footprintHint: '1x1',
    baseCost: 280,
    colours: { wall: '#c77749', roof: '#3c3430', trim: '#fff0ce', accent: '#e6a83f' }
  },
  {
    kind: 'pub',
    label: 'Pub',
    zone: 'commercial',
    capacity: 0,
    jobs: 6,
    footprintHint: '1x1',
    baseCost: 320,
    colours: { wall: '#7f4b32', roof: '#252d33', trim: '#f6ead1', accent: '#5b7f4d' }
  },
  {
    kind: 'corner-shop',
    label: 'Corner Shop',
    zone: 'commercial',
    capacity: 0,
    jobs: 4,
    footprintHint: '1x1',
    baseCost: 260,
    colours: { wall: '#b65f3c', roof: '#34424c', trim: '#f4e4c7', accent: '#4f8a77' }
  },
  {
    kind: 'supermarket',
    label: 'Mini Market',
    zone: 'commercial',
    capacity: 0,
    jobs: 12,
    footprintHint: '2x2',
    baseCost: 720,
    colours: { wall: '#d3b06d', roof: '#53626a', trim: '#f9edd6', accent: '#4f7d97' }
  },
  {
    kind: 'pharmacy',
    label: 'Pharmacy',
    zone: 'commercial',
    capacity: 0,
    jobs: 5,
    footprintHint: '1x1',
    baseCost: 300,
    colours: { wall: '#a85f4d', roof: '#32414a', trim: '#edf1d7', accent: '#5f9866' }
  },
  {
    kind: 'restaurant',
    label: 'Restaurant',
    zone: 'commercial',
    capacity: 0,
    jobs: 8,
    footprintHint: '1x2',
    baseCost: 420,
    colours: { wall: '#bc6f4e', roof: '#2d343c', trim: '#f6ead1', accent: '#c94339' }
  },
  {
    kind: 'office',
    label: 'Small Office',
    zone: 'workspace',
    capacity: 0,
    jobs: 16,
    footprintHint: '2x2',
    baseCost: 640,
    colours: { wall: '#a97955', roof: '#45535d', trim: '#e6e0cf', accent: '#5c8aa1' }
  },
  {
    kind: 'workshop',
    label: 'Maker Workshop',
    zone: 'workspace',
    capacity: 0,
    jobs: 10,
    footprintHint: '1x2',
    baseCost: 480,
    colours: { wall: '#8e6047', roof: '#39444c', trim: '#d8c8aa', accent: '#d7a541' }
  },
  {
    kind: 'library',
    label: 'Community Library',
    zone: 'workspace',
    capacity: 0,
    jobs: 6,
    footprintHint: '2x1',
    baseCost: 520,
    colours: { wall: '#b97d57', roof: '#31505c', trim: '#f7e6c5', accent: '#376e76' }
  },
  {
    kind: 'park-green',
    label: 'Pocket Park',
    zone: 'park',
    capacity: 0,
    jobs: 1,
    footprintHint: '1x1',
    baseCost: 90,
    colours: { wall: '#617d47', roof: '#6c914f', trim: '#f6ead1', accent: '#e6a83f' }
  },
  {
    kind: 'playground',
    label: 'Playground',
    zone: 'park',
    capacity: 0,
    jobs: 1,
    footprintHint: '1x1',
    baseCost: 120,
    colours: { wall: '#769d57', roof: '#86a96a', trim: '#f6ead1', accent: '#d26b49' }
  }
];

export function getBuildingDefinition(kind: BuildingKind): BuildingDefinition {
  const definition = definitions.find((item) => item.kind === kind);
  if (!definition) {
    throw new Error(`Unknown building kind: ${kind}`);
  }

  return definition;
}

export function definitionsForZone(zone: ZoneType): readonly BuildingDefinition[] {
  return definitions.filter((definition) => definition.zone === zone);
}

export function pickDefinitionForZone(zone: ZoneType, rng: () => number, cellCount: number): BuildingDefinition {
  const zoneDefinitions = definitionsForZone(zone).filter((definition) => {
    if (cellCount >= 4) {
      return definition.footprintHint !== '1x1';
    }
    return true;
  });

  return pick(zoneDefinitions.length > 0 ? zoneDefinitions : definitionsForZone(zone), rng);
}

export function getDefinitionByZoneAndKind(zone: ZoneType, kind: BuildingKind): BuildingDefinition {
  const definition = getBuildingDefinition(kind);
  if (definition.zone !== zone) {
    throw new Error(`${kind} is not valid for zone ${zone}`);
  }

  return definition;
}
